"use client";

import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, icon, id, style, className, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {label && (
          <label
            htmlFor={inputId}
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.82rem",
              fontWeight: 500,
              color: "var(--color-text)",
            }}
          >
            {label}
          </label>
        )}
        <div style={{ position: "relative" }}>
          {icon && (
            <div
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--color-text-muted)",
                display: "flex",
              }}
            >
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={className ? `ui-input ${className}` : "ui-input"}
            style={{
              width: "100%",
              padding: icon ? "10px 14px 10px 40px" : "10px 14px",
              fontFamily: "var(--font-body)",
              fontSize: "0.9rem",
              fontWeight: 400,
              color: "var(--color-text)",
              background: "#fff",
              border: `1px solid ${error ? "#e03131" : "var(--portal-card-border)"}`,
              borderRadius: 8,
              outline: "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
              ...style,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = error
                ? "#e03131"
                : "var(--color-accent)";
              e.currentTarget.style.boxShadow = error
                ? "0 0 0 3px rgba(224,49,49,0.1)"
                : "0 0 0 3px rgba(59,130,246,0.1)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = error
                ? "#e03131"
                : "var(--portal-card-border)";
              e.currentTarget.style.boxShadow = "none";
            }}
            {...props}
          />
        </div>
        {hint && !error && (
          <p
            style={{
              fontSize: "0.78rem",
              color: "var(--color-text-muted)",
              fontWeight: 400,
              margin: 0,
            }}
          >
            {hint}
          </p>
        )}
        {error && (
          <p
            style={{
              fontSize: "0.78rem",
              color: "#e03131",
              fontWeight: 500,
              margin: 0,
            }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
